/*!
VueLayers
Web map Vue components with the power of OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.10.25
@license MIT
@copyright (c) 2017-2018, Vladimir Vershinin <ghettovoice@gmail.com>
*/
'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _Object$defineProperties = _interopDefault(require('babel-runtime/core-js/object/define-properties'));
var _Object$values = _interopDefault(require('babel-runtime/core-js/object/values'));
var __ol__ = _interopDefault(require('openlayers'));
var Observable = require('rxjs/Observable');
var merge = require('rxjs/observable/merge');
var uuid = _interopDefault(require('uuid/v4'));
var olCmp = _interopDefault(require('../mixin/ol-cmp'));
var projTransforms = _interopDefault(require('../mixin/proj-transforms'));
var useMapCmp = _interopDefault(require('../mixin/use-map-cmp'));
var consts = require('../ol-ext/consts');
var observableFromOlChangeEvent = _interopDefault(require('../rx-ext/from-ol-change-event'));
var assert = require('../util/assert');
var minilo = require('../util/minilo');
var _Object$assign = _interopDefault(require('babel-runtime/core-js/object/assign'));

var Overlay = __ol__.Overlay;
var props = {
  id: {
    type: [String, Number],
    default: function _default() {
      return uuid();
    }
  },
  offset: {
    type: Array,
    default: function _default() {
      return [0, 0];
    },
    validator: function validator(value) {
      return value.length === 2;
    }
  },
  /**
   * Coordinates in the map view projection.
   * @type {number[]}
   */
  position: {
    type: Array,
    validator: function validator(value) {
      return value.length === 2;
    },
    required: true
  },
  positioning: {
    type: String,
    default: consts.OVERLAY_POSITIONING.TOP_LEFT,
    validator: function validator(value) {
      return _Object$values(consts.OVERLAY_POSITIONING).includes(value);
    }
  },
  stopEvent: {
    type: Boolean,
    default: true
  },
  insertFirst: {
    type: Boolean,
    default: true
  },
  autoPan: {
    type: Boolean,
    default: false
  },
  autoPanMargin: {
    type: Number,
    default: 20
  },
  autoPanAnimation: Object

  /**
   * @vueComputed
   */
};var computed = {
  positionViewProj: function positionViewProj() {
    if (this.rev && this.$overlay) {
      return this.$overlay.getPosition();
    }
  },
  positionDataProj: function positionDataProj() {
    if (this.rev && this.$overlay) {
      return this.pointToDataProj(this.$overlay.getPosition());
    }
  }
};

/**
 * @vueMethods
 */
var methods = /** @lends module:overlay/overlay# */{
  /**
   * @return {ol.Overlay}
   * @protected
   */
  createOlObject: function createOlObject() {
    return new Overlay({
      id: this.id,
      offset: this.offset,
      position: this.pointToViewProj(this.position),
      positioning: this.positioning,
      stopEvent: this.stopEvent,
      insertFirst: this.insertFirst,
      autoPan: this.autoPan,
      autoPanMargin: this.autoPanMargin,
      autoPanAnimation: this.autoPanAnimation
    });
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    var _this = this;

    assert.hasOverlay(this);

    this.$overlay.setElement(this.$el);
    this.$overlaysContainer && this.$overlaysContainer.addOverlay(this.$overlay);
    // reset position to trigger panIntoView
    this.$nextTick(function () {
      _this.$overlay.setPosition(_this.positionViewProj.slice());
    });
    this.subscribeAll();
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    assert.hasOverlay(this);

    this.unsubscribeAll();
    this.$overlay.setElement(undefined);
    this.$overlaysContainer && this.$overlaysContainer.removeOverlay(this.$overlay);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToOverlayChanges.call(this);
  }
};

var watch = {
  offset: function offset(value) {
    if (this.$overlay && !minilo.isEqual(value, this.$overlay.getOffset())) {
      this.$overlay.setOffset(value);
    }
  },
  position: function position(value) {
    value = this.pointToViewProj(value);
    if (this.$overlay && !minilo.isEqual(value, this.positionViewProj)) {
      this.$overlay.setPosition(value);
    }
  },
  positioning: function positioning(value) {
    if (this.$overlay && value !== this.$overlay.getPositioning()) {
      this.$overlay.setPositioning(value);
    }
  },
  resolvedDataProjection: function resolvedDataProjection() {
    if (this.$overlay) {
      this.$overlay.setPosition(this.pointToViewProj(this.position));
    }
  }
};

/**
 * @alias module:overlay/overlay
 * @title vl-overlay
 * @vueProto
 */
var Overlay$1 = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('div', { class: _vm.$options.name, attrs: { "id": [_vm.$options.name, _vm.id].join('-') } }, [_c('div', [_vm._t("default", null, { id: _vm.id, position: _vm.position, offset: _vm.offset, positioning: _vm.positioning })], 2)]);
  }, staticRenderFns: [],
  name: 'vl-overlay',
  mixins: [olCmp, useMapCmp, projTransforms],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch,
  created: function created() {
    var _this2 = this;

    _Object$defineProperties(this, /** @lends module:overlay/overlay# */{
      /**
       * @type {ol.Overlay|undefined}
       */
      $overlay: {
        enumerable: true,
        get: function get() {
          return _this2.$olObject;
        }
      },
      $map: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.map;
        }
      },
      $view: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.view;
        }
      },
      $overlaysContainer: {
        enumerable: true,
        get: function get() {
          return _this2.$services && _this2.$services.overlaysContainer;
        }
      }
    });
  }
};

/**
 * @return {void}
 * @private
 */
function subscribeToOverlayChanges() {
  var _this3 = this;

  assert.hasOverlay(this);

  var ft = 1000 / 60;
  var changes = merge.merge.call(Observable.Observable, observableFromOlChangeEvent(this.$overlay, 'position', true, ft, function () {
    return _this3.pointToDataProj(_this3.$overlay.getPosition());
  }), observableFromOlChangeEvent(this.$overlay, ['offset', 'positioning'], true, ft));

  this.subscribeTo(changes, function (_ref) {
    var prop = _ref.prop,
        value = _ref.value;

    ++_this3.rev;
    _this3.$emit('update:' + prop, value);
  });
}

/**
 * @alias module:overlay
 */
var index = {
  /**
   * @alias module:overlay/overlay
   */
  Overlay: Overlay$1,
  /**
   * @param {Vue} Vue
   * @param {VueLayersOptions} [options]
   */
  install: function install(Vue) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};

    options = minilo.pick(options, 'dataProjection');
    _Object$assign(Overlay$1, options);

    Vue.component(Overlay$1.name, Overlay$1);
  }
};

module.exports = index;



//////////////////
// WEBPACK FOOTER
// ./~/vuelayers/lib/overlay/index.js
// module id = 1479
// module chunks = 1